import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"
import {DollarSign, Eye, MapPin,  Plus, Search, Trash2, Users} from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Badge } from "@/components/ui/badge.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table.tsx"
import Pagination from "@/components/pagination.tsx"

import { AreaType, AreasFiltersType, PaginationType } from "./types.ts"

interface IndexProps {
  areas: AreaType[]
  filters: AreasFiltersType
  pagination: PaginationType
  can_create?: boolean
  can_destroy?: boolean
}

export default function Index({ areas, filters, pagination, can_create = false, can_destroy = false }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)
  const [status, setStatus] = useState(filters.status)

  const query = useMemo<Record<string, string>>(() => ({ term, status }), [term, status])

  useEffect(() => {
    setTerm(filters.term)
    setStatus(filters.status)
  }, [filters.term, filters.status])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term && status === filters.status) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/areas", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, status, filters.term, filters.status])

  const handleDestroy = (area: AreaType) => {
    if (!window.confirm(`Supprimer la zone "${area.name}" ?`)) return

    router.delete(`/admin/areas/${area.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Zones" />
      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Les Zones</h1>
            <p className="text-sm text-muted-foreground">Gestion des zones de collecte</p>
          </div>
          {can_create && (
            <Link href="/admin/areas/new">
              <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
                <Plus className="h-4 w-4" />
                Ajouter une zone
              </button>
            </Link>
          )}
        </div>


        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total des zones</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.total_count}</div>
              <p className="text-xs text-muted-foreground">Avec les filtres actuels</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total des contribuables</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0</div>
              <p className="text-xs text-muted-foreground">Indicateur</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total des collectes</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0 FCFA</div>
              <p className="text-xs text-muted-foreground">Indicateur</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Zones actives</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{areas.filter((a) => a.status === "active").length}</div>
              <p className="text-xs text-muted-foreground">Sur cette page</p>
            </CardContent>
          </Card>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Filtrer les zones par nom ou code..."
                className="pl-10"
              />
            </div>
            <Button variant="secondary" onClick={() => {
              setTerm("")
              setStatus("")
            }}>
              Réinitialiser
            </Button>
          </div>
        </div>

        <Card className="border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zone</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Agent assigné</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas.length > 0 ? (
                areas.map((area) => (
                  <TableRow key={area.id}>
                    <TableCell>
                      <Link
                        href={`/admin/areas/${area.id}`}
                        className="flex items-center gap-3 font-medium text-foreground hover:text-primary"
                      >
                        <span className="rounded-md bg-primary/10 p-2 text-primary">
                          <MapPin className="h-4 w-4" />
                        </span>
                        {area.name}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{area.code}</TableCell>
                    <TableCell className="max-w-md whitespace-normal text-muted-foreground">
                      {area.description || "Aucune description"}
                    </TableCell>
                    <TableCell>{area.assigned_agent_name || "Non assigné"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={area.status === "active" ? "default" : "secondary"}
                        className={area.status === "active" ? "bg-success/10 text-success" : undefined}
                      >
                        {area.status === "active" ? "Actif" : "Inactif"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon-sm" aria-label={`Voir ${area.name}`}>
                        <Link href={`/admin/areas/${area.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      {can_destroy && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={`Supprimer ${area.name}`}
                          onClick={() => handleDestroy(area)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    Aucune zone trouvée.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>


        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/admin/areas"
          query={query}
        />
      </div>
    </>
  )
}
